import React from 'react';
import * as d3 from 'd3';
import SvgComponent from './SvgComponent';

interface PlotProps {
    data: {alpha: number, input: number, xMax: number};
    width: number;
    height: number;
}

const PlotFunction: React.FC<PlotProps> = ({ data, width, height }) => {
    const func = (x: number, alpha): number => {
        return (2 / (1 + Math.exp(-alpha * x))) - 1;
    };

    const render = (svg: d3.Selection<SVGSVGElement, unknown, null, undefined>) => {
        const {alpha, input, xMax} = data

        const margin = { top: 20, right: 20, bottom: 40, left: 40 };

        svg.append("rect")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("fill", "white");

        const xRangePadding = 2;
        const xMin = 0;
        let xMaxPadding = xMax + xRangePadding;

        const xScale = d3.scaleLinear()
            .domain([xMin, xMaxPadding])
            .range([margin.left, width - margin.right]);

        const yScale = d3.scaleLinear()
            .domain([-1, 1])
            .range([height - margin.bottom, margin.top]);

        const line = d3.line()
            .x((d: number) => xScale(d))
            .y((d: number) => yScale(func(d, alpha)));

        const samples = d3.range(xMin, xMaxPadding, 0.01);

        svg.append('path')
            .datum(samples)
            .attr('fill', 'none')
            .attr('stroke', 'gray')
            .attr('stroke-width', 2)
            .attr('d', line);

        svg.append('g')
            .attr('transform', `translate(0,${height - margin.bottom})`)
            .call(d3.axisBottom(xScale).tickValues([0]))
            .select('.domain')
            .attr('stroke', 'black');

        svg.append('g')
            .attr('transform', `translate(${margin.left},0)`)
            .call(d3.axisLeft(yScale).tickValues([-1]))
            .select('.domain')
            .attr('stroke', 'black');

        svg.append('circle')
            .attr('cx', xScale(input))
            .attr('cy', yScale(func(input, alpha)))
            .attr('r', 5)
            .attr('fill', 'black');

        svg.append('line')
            .attr('x1', xScale(input))
            .attr('y1', yScale(func(input, alpha)))
            .attr('x2', xScale(input))
            .attr('y2', height - margin.bottom)
            .attr('stroke', 'black')
            .attr('stroke-dasharray', '4,4');

        svg.append('line')
            .attr('x1', margin.left)
            .attr('y1', yScale(func(input, alpha)))
            .attr('x2', xScale(input))
            .attr('y2', yScale(func(input, alpha)))
            .attr('stroke', 'black')
            .attr('stroke-dasharray', '4,4');

        svg.append('text')
            .attr('x', xScale(input) - 5)
            .attr('y', height - margin.bottom + 20)
            .attr('text-anchor', 'middle')
            .text(`${input}`)
            .style('font-size', '12px')
            .style('fill', 'black');

        svg.append('text')
            .attr('x', margin.left - 10)
            .attr('y', yScale(func(input, alpha)) - 5)
            .attr('text-anchor', 'end')
            .text(`${func(input, alpha).toFixed(2)}`)
            .style('font-size', '12px')
            .style('fill', 'black');
    }

    return (
        <SvgComponent
            data={data}
            render={render}
            width={width}
            height={height} />
    );
};

export default PlotFunction;
